// ============================================================
// Email Reminders registry — the single source of truth for
// reminder keys, which roles receive each one, and what control
// renders in Settings. Imported by EmailRemindersView (UI) and
// emailReminders.ts (client send service). Keep keys in sync with
// the edge functions (KEY_ROLES in send-event-reminder, the
// EXPIRING_CONFIGS keys, and the report keys).
// ============================================================

export type ReminderControl = 'toggle' | 'expiring' | 'weekly' | 'monthly';

export interface ReminderDef {
  key: string;
  label: string;
  description: string;
  category: string;
  /** Roles that RECEIVE this reminder (and can opt out in Settings). Admins/
   *  superadmins always SEE every reminder for management. */
  roles: string[];
  control: ReminderControl;
  // expiring-only:
  table?: string;
  dateColumn?: string;
  defaultLeadDays?: number;
  // schedule-only (weekly/monthly):
  defaultSchedule?: Record<string, unknown>;
}

// Superadmin receives everything; Admin only the reports (per design).
const SA = 'Superadmin';

export const REMINDERS: ReminderDef[] = [
  // ---------- Safety / Compliance (driver document expirations) ----------
  { key: 'expiring_cdl', label: 'CDL Expiration', description: 'Driver CDL expiring soon',
    category: 'Safety & Compliance', roles: ['Safety', 'Safety Manager', SA], control: 'expiring',
    table: 'drivers', dateColumn: 'cdl_expiration', defaultLeadDays: 30 },
  { key: 'expiring_medical_card', label: 'Medical Card Expiration', description: 'DOT medical card expiring',
    category: 'Safety & Compliance', roles: ['Safety', 'Safety Manager', SA], control: 'expiring',
    table: 'drivers', dateColumn: 'medical_card_expiration', defaultLeadDays: 30 },
  { key: 'expiring_mvr', label: 'MVR Review Due', description: 'Motor Vehicle Record review due',
    category: 'Safety & Compliance', roles: ['Safety', 'Safety Manager', SA], control: 'expiring',
    table: 'drivers', dateColumn: 'mvr_expiration_date', defaultLeadDays: 30 },
  { key: 'expiring_clearinghouse', label: 'Clearinghouse Query Due', description: 'FMCSA Clearinghouse query due',
    category: 'Safety & Compliance', roles: ['Safety', 'Safety Manager', SA], control: 'expiring',
    table: 'drivers', dateColumn: 'clearinghouse_expiration_date', defaultLeadDays: 30 },
  { key: 'event_inspection_created', label: 'New Inspection Logged', description: 'Email when an inspection is recorded',
    category: 'Safety & Compliance', roles: ['Safety', 'Safety Manager', 'Maintenance', SA], control: 'toggle' },
  { key: 'event_day_off_requested', label: 'Driver Day-Off Request', description: 'Email when a driver requests time off',
    category: 'Safety & Compliance', roles: ['Safety Manager', 'Dispatch Manager', SA], control: 'toggle' },

  // ---------- Recruiting ----------
  { key: 'event_driver_created', label: 'New Driver Added', description: 'Email when a driver is created',
    category: 'Recruiting', roles: ['Recruiting', SA], control: 'toggle' },

  // ---------- Dispatch ----------
  { key: 'event_load_created', label: 'New Load Created', description: 'Email when a load is created',
    category: 'Dispatch', roles: ['Dispatch', 'Dispatch Manager', SA], control: 'toggle' },
  { key: 'event_dispatch_created', label: 'New Dispatch', description: 'Email when a dispatch is created',
    category: 'Dispatch', roles: ['Dispatch', 'Dispatch Manager', SA], control: 'toggle' },

  // ---------- Maintenance ----------
  { key: 'event_maintenance_log_created', label: 'New Maintenance Log', description: 'Email when a maintenance log is created',
    category: 'Maintenance', roles: ['Maintenance', SA], control: 'toggle' },

  // ---------- Accounting ----------
  { key: 'event_invoice_sent', label: 'Invoice Generated', description: 'Email when an invoice / billing export is generated',
    category: 'Accounting', roles: ['Accounting', 'Accounting Manager', SA], control: 'toggle' },

  // ---------- Reports (Admin / Superadmin only) ----------
  { key: 'report_weekly_revenue', label: 'Weekly Revenue Report', description: 'Weekly revenue summary email',
    category: 'Reports', roles: ['Admin', SA], control: 'weekly',
    defaultSchedule: { day: 'mon', hour: 8 } },
  { key: 'report_monthly_revenue', label: 'Monthly Revenue Report', description: 'Monthly revenue summary on the 1st',
    category: 'Reports', roles: ['Admin', SA], control: 'monthly',
    defaultSchedule: { dom: 1, hour: 8 } },
];

export const LEAD_DAY_OPTIONS = [30, 60, 90];
export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => h);
export const DOW_OPTIONS: { value: string; label: string }[] = [
  { value: 'mon', label: 'Monday' }, { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' }, { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' }, { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
];
// Day-of-month limited to 1–28 so every month (incl. February) is valid.
export const DOM_OPTIONS = Array.from({ length: 28 }, (_, i) => i + 1);

export function isAdminRole(role: string | null | undefined): boolean {
  const r = (role || '').toLowerCase();
  return r === 'admin' || r === 'superadmin';
}

/** Reminders a given role can see/configure (admins see all). */
export function remindersForRole(role: string | null | undefined): ReminderDef[] {
  if (isAdminRole(role)) return REMINDERS;
  return REMINDERS.filter((r) => role != null && r.roles.includes(role));
}

/** Target roles for an event reminder (used by the client send service). */
export function rolesForKey(key: string): string[] {
  return REMINDERS.find((r) => r.key === key)?.roles ?? [];
}

export function formatHour(h: number): string {
  const period = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:00 ${period}`;
}
